import React from 'react';

import { Tabs, TabsProps } from 'antd/es';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import AreaHeader from '@app/components/UI/Area-blocks/AreaHeader/AreaHeader';
import AreaServicePattern from '@app/components/UI/Area-blocks/AreaServicePattern/AreaServicePattern';
import MailingAddress from '@app/components/UI/Area-blocks/AreaServices/MailingAddress';
import AreaServicesCommonInfo from '@app/components/UI/Area-blocks/AreaServicesCommonInfo/AreaServicesCommonInfo';
import CardPhoto from '@app/components/UI/CardPhoto/CardPhoto';
import MiddleText from '@app/components/UI/MiddleText/MiddleText';
import NavSelectMobile from '@app/components/UI/NavSelectMobile/NavSelectMobile';
import careCredit from '@app/images/CareCredit.png';
import memorialPackageImg from '@app/images/content/memorial-package.jpg';
import { Area } from '@app/objects/Area';

const navItems = [
	{ name: '0', title: 'Euthanasia' },
	{ name: '1', title: 'Aftercare & Cremation' },
	{ name: '2', title: 'Veterinary Hospice & Telehospice' },
];

const AreaServices: React.FC<{ area: Area | undefined }> = ({ area }) => {
	const [currentIndex, setCurrentIndex] = React.useState('0');

	const clickOnAftercare = () => {
		setCurrentIndex('1');
		document.querySelector('.area-services div[role="tablist"]')?.scrollIntoView();
	};

	const tabs: TabsProps['items'] = [
		{
			key: '0',
			label: 'Euthanasia',
			children: (
				<>
					<AreaServicePattern
						title="Euthanasia appointment"
						description={area?.areaService?.euthanasiaDescription}
						textLink="Learn more about euthanasia"
						textLinkUrl="/our-services/in-home-euthanasia"
						fees={[{ caption: 'Appointment Base Pricing', price: area?.areaService?.euthanasiaPricing }]}
						pricePostscript={
							<>
								*Additional fees may apply including
								{' '}
								<span className="link" onClick={clickOnAftercare}>
									aftercare
								</span>
							</>
						}
					/>
					<CardPhoto imgURL={memorialPackageImg} title="A look at our pet memorial package." />
					<AreaServicesCommonInfo additionalServices={area?.areaService?.additionalServices} />
				</>
			),
		},
		{
			key: '1',
			label: 'Aftercare & Cremation',
			children: (
				<>
					<AreaServicePattern
						title="Private cremation"
						description={area?.areaService?.privateCremationDescription}
						textLink="Learn more about aftercare"
						textLinkUrl="/our-services/aftercare"
						fees={area?.areaService?.privateCremation?.map((cremation) => {
							return {
								price: cremation.fee,
								caption: `Pets ${cremation.minWeight ? `${cremation.minWeight} -` : 'up to'} ${cremation.maxWeight} pounds`,
							};
						})}
					/>
					<AreaServicePattern
						title="Communal cremation"
						description={area?.areaService?.communalCremationDescription}
						textLink="Learn more about aftercare"
						textLinkUrl="/our-services/aftercare"
						fees={area?.areaService?.communalCremation?.map((cremation) => {
							return {
								price: cremation.fee,
								caption: `Pets ${cremation.minWeight ? `${cremation.minWeight} -` : 'up to'} ${cremation.maxWeight} pounds`,
							};
						})}
					/>
					<AreaServicesCommonInfo
						additionalServices={area?.areaService?.additionalServices}
						customReplacements={[{ name: 'Additional fees', description: area?.areaService?.aftercareAdditionalFees || '' }]}
					/>
				</>
			),
		},
		{
			key: '2',
			label: 'Veterinary Hospice & Telehospice',
			children: (
				<>
					<AreaServicePattern
						title="Veterinary hospice appointment"
						description={area?.areaService?.hospiceDescription}
						textLink="Learn more about veterinary hospice"
						textLinkUrl="/our-services/veterinary-hospice"
						fees={[{ caption: 'Appointment Base Pricing', price: area?.areaService?.hospicePricing }]}
						pricePostscript={
							<>
								*Additional fees may apply including
								{' '}
								<span className="link" onClick={clickOnAftercare}>
									aftercare
								</span>
							</>
						}
					/>
					<AreaServicePattern
						title="Telehospice appointment"
						description={area?.areaService?.teleadviceDescription}
						textLink="Learn more about telehospice"
						textLinkUrl="/our-services/telehospice"
						fees={[{ caption: 'Telehospice Appointment Pricing', price: area?.areaService?.teleadvicePricing }]}
					/>
					<AreaServicesCommonInfo additionalServices={area?.areaService?.additionalServices} />
				</>
			),
		},
	];

	const onItemSelect = (e: string) => {
		setCurrentIndex(tabs.find((tab) => tab.label === e).key);
	};

	return (
		<div className="area">
			<AreaHeader area={area} />
			<NavSelectMobile navItems={navItems} onChange={onItemSelect} currentIndex={currentIndex} />
			{!area?.areaService ? (
				<BounceLoader />
			) : (
				<>
					<Tabs
						className="area-services"
						activeKey={currentIndex}
						onTabClick={(key, event) => {
							setCurrentIndex(key);
						}}
						items={tabs}
					/>

					{area?.areaService?.paymentOptions && (
						<div>
							<MiddleText title="Payment options" text={area?.areaService.paymentOptions} />
							<div>
								<img src={careCredit} alt="Care Credit a Synchrony solution" className="care-credit-img" />
							</div>
						</div>
					)}

					<MailingAddress area={area} />
				</>
			)}
		</div>
	);
};

export default AreaServices;
